import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Slider,
  Button,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const ProductFilter = ({
  filters,
  onFiltersChange,
  onClearFilters,
  maxPrice = 1000,
  categories = [],
  farmingMethods = [],
  availableRatings = [5, 4, 3, 2, 1]
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...localFilters };
    
    if (filterType === 'categories' || filterType === 'farmingMethods' || filterType === 'ratings') {
      if (newFilters[filterType].includes(value)) {
        newFilters[filterType] = newFilters[filterType].filter(item => item !== value);
      } else {
        newFilters[filterType] = [...newFilters[filterType], value];
      }
    } else if (filterType === 'priceRange') {
      newFilters.priceRange = value;
    } else {
      newFilters[filterType] = value;
    }
    
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearAll = () => {
    const clearedFilters = {
      categories: [],
      farmingMethods: [],
      priceRange: [0, maxPrice],
      ratings: [],
      inStock: false,
      featured: false,
      organic: false
    };
    setLocalFilters(clearedFilters);
    onClearFilters(clearedFilters);
  };

  const getActiveFiltersCount = () => {
    return (
      localFilters.categories.length +
      localFilters.farmingMethods.length +
      localFilters.ratings.length +
      (localFilters.inStock ? 1 : 0) +
      (localFilters.featured ? 1 : 0) +
      (localFilters.organic ? 1 : 0) +
      (localFilters.priceRange[0] > 0 || localFilters.priceRange[1] < maxPrice ? 1 : 0)
    );
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Filters
            {getActiveFiltersCount() > 0 && (
              <Chip
                label={getActiveFiltersCount()}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          {getActiveFiltersCount() > 0 && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          )}
        </Box>

        {/* Quick Filters */}
        <Box sx={{ mb: 2 }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFilters.inStock}
                  onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                />
              }
              label="In Stock"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFilters.featured}
                  onChange={(e) => handleFilterChange('featured', e.target.checked)}
                />
              }
              label="Featured"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={localFilters.organic}
                  onChange={(e) => handleFilterChange('organic', e.target.checked)}
                />
              }
              label="Organic"
            />
          </FormGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Price Range */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Price Range</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ px: 1 }}>
              <Slider
                value={localFilters.priceRange}
                onChange={(e, value) => handleFilterChange('priceRange', value)}
                valueLabelDisplay="auto"
                min={0}
                max={maxPrice}
                marks={[
                  { value: 0, label: '₹0' },
                  { value: maxPrice / 2, label: `₹${maxPrice / 2}` },
                  { value: maxPrice, label: `₹${maxPrice}` }
                ]}
                valueLabelFormat={(value) => `₹${value}`}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2">
                  ₹{localFilters.priceRange[0]}
                </Typography>
                <Typography variant="body2">
                  ₹{localFilters.priceRange[1]}
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Categories */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Categories</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {categories.map(category => (
                <FormControlLabel
                  key={category}
                  control={
                    <Checkbox
                      checked={localFilters.categories.includes(category)}
                      onChange={() => handleFilterChange('categories', category)}
                    />
                  }
                  label={category.replace('_', ' ')}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Farming Methods */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Farming Method</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {farmingMethods.map(method => (
                <FormControlLabel
                  key={method}
                  control={
                    <Checkbox
                      checked={localFilters.farmingMethods.includes(method)}
                      onChange={() => handleFilterChange('farmingMethods', method)}
                    />
                  }
                  label={method}
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Ratings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Customer Rating</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormGroup>
              {availableRatings.map(rating => (
                <FormControlLabel
                  key={rating}
                  control={
                    <Checkbox
                      checked={localFilters.ratings.includes(rating)}
                      onChange={() => handleFilterChange('ratings', rating)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={rating} readOnly size="small" />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        & above
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {/* Applied Filters Summary */}
        {getActiveFiltersCount() > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Applied Filters:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {localFilters.categories.map(category => (
                <Chip
                  key={`cat-${category}`}
                  label={category.replace('_', ' ')}
                  size="small"
                  onDelete={() => handleFilterChange('categories', category)}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {localFilters.farmingMethods.map(method => (
                <Chip
                  key={`method-${method}`}
                  label={method}
                  size="small"
                  onDelete={() => handleFilterChange('farmingMethods', method)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
              {localFilters.ratings.map(rating => (
                <Chip
                  key={`rating-${rating}`}
                  label={`${rating}+ stars`}
                  size="small"
                  onDelete={() => handleFilterChange('ratings', rating)}
                  color="warning"
                  variant="outlined"
                />
              ))}
              {localFilters.inStock && (
                <Chip
                  label="In Stock"
                  size="small"
                  onDelete={() => handleFilterChange('inStock', false)}
                  color="success"
                  variant="outlined"
                />
              )}
              {localFilters.featured && (
                <Chip
                  label="Featured"
                  size="small"
                  onDelete={() => handleFilterChange('featured', false)}
                  color="info"
                  variant="outlined"
                />
              )}
              {localFilters.organic && (
                <Chip
                  label="Organic"
                  size="small"
                  onDelete={() => handleFilterChange('organic', false)}
                  color="success"
                  variant="outlined"
                />
              )}
              {(localFilters.priceRange[0] > 0 || localFilters.priceRange[1] < maxPrice) && (
                <Chip
                  label={`₹${localFilters.priceRange[0]} - ₹${localFilters.priceRange[1]}`}
                  size="small"
                  onDelete={() => handleFilterChange('priceRange', [0, maxPrice])}
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductFilter;